/**
 * Helpers for the room-mode calculator stem plot.
 */

import { withAlpha, type DesignToken } from '@roomtreatment/design';
import type { RoomMode } from '@/lib/acousticsPhysics';

export interface ModeCluster {
  /** Mean frequency of the cluster. */
  centerHz: number;
  /** Modes within the window (sorted by freq). */
  modes: RoomMode[];
}

/**
 * Group modes that sit within `windowHz` of a neighbor (bad modal density).
 * Only returns clusters with 2+ modes.
 */
export function findModeClusters(modes: RoomMode[], windowHz = 5): ModeCluster[] {
  if (modes.length < 2) return [];
  const sorted = [...modes].sort((a, b) => a.freqHz - b.freqHz);
  const clusters: ModeCluster[] = [];
  let run: RoomMode[] = [sorted[0]];

  const flush = () => {
    if (run.length >= 2) {
      const centerHz = run.reduce((s, m) => s + m.freqHz, 0) / run.length;
      clusters.push({ centerHz, modes: run });
    }
    run = [];
  };

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (cur.freqHz - prev.freqHz <= windowHz) {
      if (run.length === 0) run.push(prev);
      run.push(cur);
    } else {
      flush();
      run = [cur];
    }
  }
  flush();
  return clusters;
}

/**
 * The mode whose frequency is closest to `hz`, in log-space distance (a
 * decade at 20 Hz and a decade at 200 Hz should read as equally "near" —
 * matches `nearestValueAtFreq` in @roomtreatment/charts). Always returns the
 * closest mode, with no proximity cutoff: the shared chart cursor reads
 * anywhere over the plot, so the readout should always say something rather
 * than going silent in the gaps between stems. Null only for an empty list.
 */
export function nearestMode(modes: RoomMode[], hz: number): RoomMode | null {
  let best: RoomMode | null = null;
  let bestD = Infinity;
  for (const m of modes) {
    if (m.freqHz <= 0 || hz <= 0) continue;
    const d = Math.abs(Math.log10(m.freqHz) - Math.log10(hz));
    if (d < bestD) {
      bestD = d;
      best = m;
    }
  }
  return best;
}

/** Relative stem height 0–1 by classical energy weight. */
export function modeStemHeight(type: RoomMode['type']): number {
  if (type === 'axial') return 1;
  if (type === 'tangential') return 0.62;
  return 0.34;
}

/**
 * Stem colour by mode type: the accent for AXIAL, ink at two weights for the
 * rest.
 *
 * Axial modes are the ones a user acts on — they carry the most energy
 * (`modeStemHeight` above says so) and they are what treatment targets. So they
 * get the accent, and the two weaker families get ink at decreasing weight,
 * which reads as an ordered sequence in a way the old gold/cream/grey triple
 * did not. Every value is concrete rather than a `var()`: this feeds
 * `ctx.strokeStyle`, and a canvas cannot resolve a custom property (see
 * tests/unit/canvasColours.test.ts).
 *
 * `t` is the token set in force at the canvas — the caller passes
 * `tokensAt(canvas)` at draw time, so the stems follow a theme flip.
 */
export function modeStemColor(
  type: RoomMode['type'],
  muted: boolean,
  t: Record<DesignToken, string>,
): string {
  if (type === 'axial') return withAlpha(t.accent, muted ? 0.28 : 1);
  if (type === 'tangential') return withAlpha(t.ink, muted ? 0.22 : 0.72);
  return withAlpha(t.ink, muted ? 0.14 : 0.4);
}
